import React from 'react';

import { ClaudeIcon } from '@atlassian/logo-third-party/claude';
import { CursorIcon } from '@atlassian/logo-third-party/cursor';
import { OpenAIIcon } from '@atlassian/logo-third-party/openai';

export type ShareContextToolLogo = (props: {
	size: 'xsmall' | 'small' | 'medium';
}) => React.JSX.Element;

/** Where a surface opens the prompt. Also the value the tool click event reports as `target`. */
export type ShareContextTargetId = 'desktop' | 'web';

export type ShareContextTarget = {
	id: ShareContextTargetId;
	/** Builds the deep link for an already-encoded prompt. */
	buildHref: (encodedPrompt: string) => string;
	/**
	 * `codex:` and `claude:` are protocol handlers: the OS takes the hand-off and the page stays
	 * put, so a new tab would be left blank. Only `https:` links need one.
	 */
	opensInNewTab: boolean;
};

export type ShareContextTool = {
	id: string;
	/** Product names are not translated, so they are plain strings, not messages. */
	name: string;
	Logo: ShareContextToolLogo;
	/** In menu order. Non-empty by type: a tool needs somewhere to send the prompt. */
	targets: readonly [ShareContextTarget, ...ShareContextTarget[]];
};

// `logo-third-party` always tiles its glyphs and has no borderless variant, so
// `ShareContextRovoItem` tiles the Rovo mark too rather than the menu carrying two treatments.
//
// ChatGPT uses the OpenAI mark because `logo-third-party` has no `chatgpt` entry point.
const ChatGptLogo: ShareContextToolLogo = ({ size }) => <OpenAIIcon label="" size={size} />;

const ClaudeLogo: ShareContextToolLogo = ({ size }) => <ClaudeIcon label="" size={size} />;

const CursorLogo: ShareContextToolLogo = ({ size }) => <CursorIcon label="" size={size} />;

/** The AI surfaces the share-context menu offers, in display order. */
export const SHARE_CONTEXT_TOOLS: ShareContextTool[] = [
	{
		id: 'cursor',
		name: 'Cursor',
		Logo: CursorLogo,
		targets: [
			{
				// Lands on a cursor.com interstitial that opens the app, or offers a download. Still
				// `desktop` because that is where the prompt is headed.
				id: 'desktop',
				buildHref: (encodedPrompt) => `https://cursor.com/link/prompt?text=${encodedPrompt}`,
				opensInNewTab: true,
			},
		],
	},
	{
		id: 'claude',
		name: 'Claude',
		Logo: ClaudeLogo,
		targets: [
			{
				id: 'desktop',
				buildHref: (encodedPrompt) => `claude://code/new?q=${encodedPrompt}`,
				opensInNewTab: false,
			},
			{
				id: 'web',
				buildHref: (encodedPrompt) => `https://claude.ai/new?q=${encodedPrompt}`,
				opensInNewTab: true,
			},
		],
	},
	{
		id: 'chatgpt',
		name: 'ChatGPT',
		Logo: ChatGptLogo,
		targets: [
			{
				id: 'desktop',
				buildHref: (encodedPrompt) => `codex://new?prompt=${encodedPrompt}`,
				opensInNewTab: false,
			},
			{
				id: 'web',
				buildHref: (encodedPrompt) => `https://chatgpt.com/?prompt=${encodedPrompt}`,
				opensInNewTab: true,
			},
		],
	},
];

export type ShareContextToolTarget = { tool: ShareContextTool; target: ShareContextTarget };

/** Every tool's targets as one list, in menu order: the menu gives each target its own item. */
export const SHARE_CONTEXT_TOOL_TARGETS: readonly ShareContextToolTarget[] =
	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total target count; built once from a fixed list of three tools
	SHARE_CONTEXT_TOOLS.flatMap((tool) => tool.targets.map((target) => ({ tool, target })));
